/**
 * Realisatie-sectie (TrackJack): dag-tijdlijn van de ritten + week-tabel
 * gepland (RouteLogic) vs gereden (TrackJack). Staat onder het Team
 * Leaderboard / Diensten Breakdown; periode-onafhankelijk (vandaag + de
 * afgelopen 7 dagen). Verbergt zichzelf zolang TrackJack niet gekoppeld is.
 * Data uit /api/schoorsteenveger/realisatie (server cachet 10 min).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import InfoDot from '../shared/InfoDot';
import { EMPLOYEES } from '../../constants/schoorsteenvegerLogic';
import { getRealisatie } from '../../utils/schoorsteenvegerQueries';

const NUM = { fontVariantNumeric: 'tabular-nums' };

const empColor = (naam) => {
  const d = String(naam || '').toLowerCase().trim();
  const emp = Object.values(EMPLOYEES).find((e) => {
    const full = e.name.toLowerCase();
    return d === full || d === full.split(' ')[0];
  });
  return emp ? emp.color : 'var(--color-neutral-400)';
};

const fmtDur = (min) => {
  const m = Math.round(min || 0);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// Minuten-sinds-middernacht -> kloktijd ("452" -> "07:32")
const clock = (min) => (min == null ? null : fmtDur(min));

// Delta in leesbare vorm voor tooltips: 62 -> "1:02 later", -30 -> "0:30 eerder"
const deltaWoord = (d) => {
  if (d == null || Math.abs(d) < 1) return 'op plan';
  const t = fmtDur(Math.abs(d)).replace(/^0/, '');
  return d > 0 ? `${t} later` : `${t} eerder`;
};

const DAGEN = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const dayLabel = (ymd) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  return `${DAGEN[d.getUTCDay()]} ${ymd.slice(8, 10)}-${ymd.slice(5, 7)}`;
};

const INFO_TEKST = 'Bron: TrackJack (ritregistratie). De tijdlijn toont de ritten van vandaag per monteur; '
  + 'de gaten ertussen is stilstand, bij een klant of pauze. In de week-tabel staat de planning '
  + '(RouteLogic: geplande vertrek- en thuiskomsttijd + stops) naast de werkelijkheid (eerste vertrek '
  + 't/m einde laatste rit). Hover op "Op pad" toont hoeveel eerder of later er vertrokken en '
  + 'thuisgekomen is dan gepland. Rijtijd = werkelijk gereden, met tussen haakjes de geplande rijtijd '
  + 'uit RouteLogic. Verschil = werkelijke spanne minus geplande routeduur: rood = langer onderweg dan '
  + 'gepland. Oranje gemarkeerde regels: de bus is gebruikt zonder dat er een planning stond, terwijl '
  + 'de afspraak is dat de bussen niet privé gereden worden.';

// ── Dag-tijdlijn: 07:00–19:00, één track per monteur ─────────────────────────
const T0 = 7 * 60;
const T1 = 19 * 60;
const SPAN = T1 - T0;

const UREN = [8, 10, 12, 14, 16, 18];

const Timeline = ({ timeline }) => {
  const namen = Object.keys(timeline || {}).sort();
  if (!namen.length) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', rowGap: '10px', alignItems: 'center' }}>
      {namen.map((naam) => (
        <React.Fragment key={naam}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 700 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: empColor(naam), flexShrink: 0 }} />
            {naam}
          </div>
          <div style={{ position: 'relative', height: '22px', background: 'var(--color-neutral-100)', borderRadius: '6px', overflow: 'hidden' }}>
            {/* Subtiel uur-raster, gelijk aan de as-labels eronder */}
            {UREN.map((h) => (
              <span key={h} style={{ position: 'absolute', top: 0, bottom: 0, width: '1px', background: 'var(--color-neutral-200)', left: `${((h * 60 - T0) / SPAN) * 100}%` }} />
            ))}
            {(timeline[naam] || []).map((t, i) => {
              const clampedStart = Math.max(T0, t.startMin);
              const clampedEnd = Math.min(T1, Math.max(t.endMin, t.startMin + 2));
              if (clampedEnd <= T0 || clampedStart >= T1) return null;
              return (
                <div
                  key={i}
                  title={`${t.start}–${t.end} · ${t.km} km`}
                  style={{
                    position: 'absolute', top: '4px', bottom: '4px', borderRadius: '4px',
                    left: `${((clampedStart - T0) / SPAN) * 100}%`,
                    width: `${Math.max(0.6, ((clampedEnd - clampedStart) / SPAN) * 100)}%`,
                    background: empColor(naam),
                  }}
                />
              );
            })}
          </div>
        </React.Fragment>
      ))}
      <div />
      <div style={{ position: 'relative', height: '14px', fontSize: '0.66rem', color: 'var(--color-neutral-400)' }}>
        {UREN.map((h) => (
          <span key={h} style={{ position: 'absolute', left: `${((h * 60 - T0) / SPAN) * 100}%`, transform: 'translateX(-50%)', ...NUM }}>
            {String(h).padStart(2, '0')}:00
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Week-tabel: gepland vs gereden ───────────────────────────────────────────
const th = { textAlign: 'right', padding: '7px 10px', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)', whiteSpace: 'nowrap', textTransform: 'uppercase' };
const td = { textAlign: 'right', padding: '7px 10px', fontSize: '0.78rem', whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-neutral-100)', ...NUM };

// Stille cel: geen waarde. Bewust een gewoon koppelteken (huisstijl: geen
// kastlijntjes in NL-teksten).
const Leeg = () => <span style={{ color: 'var(--color-neutral-400)' }}>-</span>;

const RealisatieSection = ({ isDark = false }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const res = await getRealisatie();
      if (!stopped) setData(res && res.connected ? res : null);
    };
    tick();
    const iv = setInterval(tick, 5 * 60 * 1000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    (data.days || []).forEach((d) => {
      d.rows.forEach((r, i) => out.push({ date: d.date, first: i === 0, ...r }));
    });
    return out;
  }, [data]);

  if (!data) return null;

  const heeftTimeline = Object.values(data.timeline || {}).some((t) => t.length > 0);

  return (
    <div className="card-blue" style={{ marginTop: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title">Gereden vs. gepland</h3>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', color: 'var(--color-neutral-500)', whiteSpace: 'nowrap' }}>
          TrackJack
          <InfoDot text={INFO_TEKST} />
        </span>
      </div>
      <div className="card-body">
        {heeftTimeline && (
          <div style={{ borderBottom: '1px solid var(--color-neutral-200)', paddingBottom: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
            <Timeline timeline={data.timeline} />
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Dag</th>
                <th style={{ ...th, textAlign: 'left' }}>Monteur</th>
                <th style={th}>Gepland</th>
                <th style={th}>Op pad</th>
                <th style={th}>Verschil</th>
                <th style={th}>Rijtijd</th>
                <th style={th}>Km</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const p = r.planned;
                const w = r.actual;
                // Gereden zonder planning: de bussen worden volgens afspraak
                // niet privé gebruikt, dus dit moet opvallen.
                const zonderPlanning = !!w && !p;
                let verschil = <Leeg />;
                if (p && w && p.plandur > 0) {
                  const dm = w.spanMin - p.plandur;
                  verschil = (
                    <span
                      title={`Werkelijk op pad ${fmtDur(w.spanMin)} vs gepland ${fmtDur(p.plandur)}`}
                      style={{ color: dm > 15 ? '#dc2626' : '#059669', fontWeight: 700, cursor: 'help' }}
                    >
                      {dm >= 0 ? '+' : '-'}{fmtDur(Math.abs(dm))}
                    </span>
                  );
                }
                const rowStyle = {
                  ...(r.first ? { borderTop: '1px solid var(--color-neutral-200)' } : null),
                  ...(zonderPlanning ? { background: isDark ? 'rgba(245, 158, 11, 0.10)' : '#fffbeb' } : null),
                };
                // Geplande rijtijd = geplande routeduur minus geplande klustijd
                const planTravel = p && p.plandur > 0 && p.workdur != null
                  ? Math.max(0, p.plandur - p.workdur) : null;
                // Tooltip op "Op pad": vertrek/thuiskomst vs de geplande ETA's
                const opPadTitle = (w && p && p.planStart != null && p.planEnd != null)
                  ? `Gepland ${clock(p.planStart)}–${clock(p.planEnd)} · vertrek ${deltaWoord(w.eersteMin - p.planStart)}, thuis ${deltaWoord(w.laatsteMin - p.planEnd)}`
                  : undefined;
                return (
                  <tr key={r.date + r.naam} style={rowStyle}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.first ? dayLabel(r.date) : ''}</td>
                    <td style={{ ...td, textAlign: 'left' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: empColor(r.naam), marginRight: 6 }} />
                      {r.naam}
                    </td>
                    <td style={td}>
                      {p ? (
                        <>
                          {p.planStart != null && p.planEnd != null ? `${clock(p.planStart)}–${clock(p.planEnd)}` : fmtDur(p.plandur)}
                          <span style={{ color: 'var(--color-neutral-400)', marginLeft: 6 }}>· {p.stops} stops</span>
                        </>
                      ) : (
                        zonderPlanning ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: isDark ? '#fbbf24' : '#b45309', fontWeight: 700 }}>
                            <AlertTriangle size={12} strokeWidth={2.4} aria-hidden />
                            gereden zonder planning
                          </span>
                        ) : <Leeg />
                      )}
                    </td>
                    <td style={{ ...td, ...(opPadTitle ? { cursor: 'help' } : null) }} title={opPadTitle}>
                      {w ? `${w.eerste}–${w.laatste}` : <span style={{ color: 'var(--color-neutral-400)' }}>niet gereden</span>}
                    </td>
                    <td style={td}>{verschil}</td>
                    <td style={td} title={planTravel != null ? `Werkelijke rijtijd ${w ? fmtDur(w.rijtijd) : '-'} vs gepland ${fmtDur(planTravel)} (RouteLogic)` : undefined}>
                      {w ? fmtDur(w.rijtijd) : <Leeg />}
                      {planTravel != null && (
                        <span style={{ color: 'var(--color-neutral-400)', marginLeft: 5 }}>({fmtDur(planTravel)})</span>
                      )}
                    </td>
                    <td style={td}>{w ? w.km : <Leeg />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RealisatieSection;
